import React, { Fragment } from "react";
import LockIcon from "@mui/icons-material/Lock";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import UtilService from "../../../services/UtilService";
import CustomSteteChip from "../../components/CustomStateChip";
import { extractTotalScore, getWheelCharactersFromWords } from "../wordWheelDisplayUtils";
import {
    extractSeasonFromRow,
    getDisplayPuzzleLevel,
    hasAssignedPuzzleLevel,
    isLevelLocked,
} from "../wordWheelLevelUtils";
import WordWheelMissingClueFlag from "./WordWheelMissingClueFlag";

function parseWordsInUse(wordsInUse) {
    return (wordsInUse || "")
        .split(/[\n,;]+/)
        .map((w) => w.trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

export function getColumns({ onFillMissingClues, fillingClueId } = {}) {
    return [
        {
            field: "puzzleLevel",
            headerName: "Level",
            flex: 0.3,
            minWidth: 88,
            valueGetter: (value, row) => (hasAssignedPuzzleLevel(row) ? getDisplayPuzzleLevel(row) : null),
            renderCell: (params) => {
                const locked = isLevelLocked(params.row);
                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <span>{params.value != null ? params.value : "—"}</span>
                        {locked && (
                            <Tooltip title="Level locked — season catalog is complete">
                                <LockIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                            </Tooltip>
                        )}
                    </Box>
                );
            },
        },
        { field: "title", headerName: "Title", flex: 0.35, minWidth: 100 },
        {
            field: "words",
            headerName: "Words",
            flex: 1.4,
            minWidth: 220,
            sortable: true,
            valueGetter: (value, row) => parseWordsInUse(row?.wordsInUse).join(", "),
            renderCell: (params) => {
                const words = parseWordsInUse(params.row?.wordsInUse);
                if (words.length === 0) {
                    return "—";
                }
                const bannedHits = Array.isArray(params.row?.bannedHits)
                    ? params.row.bannedHits
                    : [];
                const bannedSet = new Set(
                    bannedHits.map((w) => String(w).toLowerCase()).filter(Boolean),
                );
                const label = words.join(", ");
                const title =
                    bannedHits.length > 0
                        ? `Banned: ${bannedHits.join(", ")}\n\n${label}`
                        : label;
                return (
                    <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{title}</span>} enterDelay={400}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                height: "100%",
                                width: "100%",
                                minWidth: 0,
                            }}
                        >
                            {bannedHits.length > 0 ? (
                                <Chip
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    label={bannedHits.length}
                                    sx={{ height: 22, flexShrink: 0 }}
                                />
                            ) : null}
                            <WordWheelMissingClueFlag
                                row={params.row}
                                filling={fillingClueId === params.row?.id}
                                onFill={onFillMissingClues}
                            />
                            <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    width: "100%",
                                    ...(bannedSet.size > 0
                                        ? {
                                              "& mark": {
                                                  backgroundColor: "rgba(211, 47, 47, 0.18)",
                                                  color: "error.main",
                                                  fontWeight: 700,
                                                  borderRadius: 0.5,
                                                  px: 0.25,
                                              },
                                          }
                                        : null),
                                }}
                            >
                                {bannedSet.size === 0
                                    ? label
                                    : words.map((word, idx) => (
                                          <Fragment key={`${word}-${idx}`}>
                                              {idx > 0 ? ", " : null}
                                              {bannedSet.has(word.toLowerCase()) ? (
                                                  <mark>{word}</mark>
                                              ) : (
                                                  word
                                              )}
                                          </Fragment>
                                      ))}
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: "state",
            headerName: "State",
            flex: 0.35,
            renderCell: (params) => <CustomSteteChip label={params.value} size="small" />,
        },
        {
            field: "season",
            headerName: "Season",
            flex: 0.45,
            sortable: false,
            valueGetter: (value, row) => extractSeasonFromRow(row),
            renderCell: (params) => (
                params.value
                    ? <Chip label={params.value} size="small" variant="outlined" />
                    : "—"
            ),
        },
        {
            field: "difficultyLevel",
            headerName: "Difficulty",
            flex: 0.35,
            renderCell: (params) => (params.value ? <Chip label={params.value} size="small" /> : "—"),
        },
        {
            field: "totalScore",
            headerName: "Total Score",
            flex: 0.35,
            sortable: false,
            valueGetter: (value, row) => extractTotalScore(row?.details),
            renderCell: (params) => (params.value != null ? params.value.toLocaleString() : "—"),
        },
        {
            field: "characterCount",
            headerName: "Character Count",
            flex: 0.3,
            sortable: false,
            valueGetter: (value, row) => getWheelCharactersFromWords(row?.wordsInUse).length,
            renderCell: (params) => (params.value > 0 ? params.value : "—"),
        },
        {
            field: "createdAt",
            headerName: "Created",
            flex: 0.4,
            valueGetter: (value) => (value ? UtilService.formatDate(value) : ""),
        },
        {
            field: "publishedAt",
            headerName: "Published",
            flex: 0.4,
            valueGetter: (value) => (value ? UtilService.formatDate(value) : ""),
        },
    ];
}
